import { Plus, Trash2 } from 'lucide-react'
import { useFieldArray, type Control } from 'react-hook-form'

import { SelectField, TextField } from '@/components/form/fields'
import { Button } from '@/components/ui/button'

import { RELATIONSHIP_OPTIONS } from '../constants'
import type { CustomerWizardInput } from '../validation'
import { FormattedTextField } from './formatted-field'

const tidyPhone = (raw: string) => raw.replace(/[\s()\-.]/g, '')

const BLANK_ROW = { fullName: '', relationship: '', phoneNumber: '', address: '' }

/**
 * Next of kin.
 *
 * Optional, and left optional: nothing in the rules requires it, and a screen that invented a
 * requirement here would teach operators to type a placeholder into a field someone will one day
 * telephone. A row that has been started, though, must be finished — a name with no number is
 * worse than an empty entry, because it looks like a contact until someone needs it.
 */
export function NextOfKinStep({ control }: { control: Control<CustomerWizardInput> }) {
  const { fields, append, remove } = useFieldArray({ control, name: 'nextOfKin' })

  return (
    <div className="space-y-4">
      <p className="max-w-2xl text-sm text-body">
        Who to contact when the customer cannot be reached. Leave it empty if the customer would
        rather not say — it can be added at any later visit.
      </p>

      {fields.map((row, index) => (
        <fieldset
          key={row.id}
          className="space-y-3 rounded-card border border-border-subtle bg-surface p-4"
        >
          <div className="flex items-center justify-between gap-2">
            <legend className="text-sm font-semibold text-heading">
              Contact {index + 1}
            </legend>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => remove(index)}
              aria-label={`Remove contact ${index + 1}`}
            >
              <Trash2 />
              Remove
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              control={control}
              name={`nextOfKin.${index}.fullName`}
              label="Full name"
              autoComplete="off"
            />
            <SelectField
              control={control}
              name={`nextOfKin.${index}.relationship`}
              label="Relationship"
              options={RELATIONSHIP_OPTIONS}
              placeholder="Select…"
            />
            <FormattedTextField
              control={control}
              name={`nextOfKin.${index}.phoneNumber`}
              label="Mobile number"
              inputMode="tel"
              placeholder="0241234567"
              format={tidyPhone}
            />
            <TextField
              control={control}
              name={`nextOfKin.${index}.address`}
              label="Address"
              description="Enough to find them. A town and a landmark is usually enough."
            />
          </div>
        </fieldset>
      ))}

      <Button type="button" variant="secondary" onClick={() => append(BLANK_ROW)}>
        <Plus />
        {fields.length === 0 ? 'Add a contact' : 'Add another contact'}
      </Button>
    </div>
  )
}
